<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, computed, onMounted } from 'vue';

/*==================================================================*/
/*================== IMPORTS DOS COMPONENTES =======================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ModalConfirmacaoFinalizarJob from '@/views/JobFinalizar/ModalConfirmacaoFinalizarJob/ModalConfirmacaoFinalizarJob.vue';
import ModalErroValidacaoJob from '@/views/JobFinalizar/ModalErroValidacaoJob/ModalErroValidacaoJob.vue';

/*==================================================================*/
/*================== IMPORTS DE SERVICES ===========================*/
/*==================================================================*/

import JobFinalizarService from '@/api/v2/services/JobFinalizarService.js';

/*==================================================================*/
/*================== IMPORTS DE UTILS ==============================*/
/*==================================================================*/

import UtilArray from '@/utils/UtilArray.mjs';

/*==================================================================*/
/*================== MODAIS DE CONFIRMAÇÃO E ERRO ==================*/
/*==================================================================*/

const modalConfirmacaoFinalizarJobRef = ref(null);
const modalErroValidacaoJobRef = ref(null);

/*==================================================================*/
/*================== CONTROLES DESATIVADOS =========================*/
/*==================================================================*/

const disable = ref(false);

const alteraDisable = (update = false) => {

    disable.value = update;

};

/*==================================================================*/
/*================== DADOS DE JOBS =================================*/
/*==================================================================*/

const jobs = ref([]);
const dados = ref([]);

/*==================================================================*/
/*================== JOB SELECIONADO NA GRID ========================*/
/*==================================================================*/

const selecionado = ref({});

const alteraSelecionado = (dado = {}) => {

    selecionado.value = dado;

};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = (dadosParaPreparar = []) => {

    dados.value = dadosParaPreparar.map((dado = {}) => ({
        id: dado.id ?? '',
        codigo: dado.codigo ?? '',
        status: dado.status ?? '',
        descricao: dado.descricao ?? '',
        cliente: dado.cliente ?? {},
        dataFinalContratual: dado.dataFinalContratual ?? '',
        dataFinalEfetiva: dado.dataFinalEfetiva ?? '',
    }));

};

/*==================================================================*/
/*================== MÉTODOS DE LOAD DAS VIEWS =====================*/
/*==================================================================*/

const loadJobs = async () => {

    alteraDisable(true);

    const response = await JobFinalizarService.pegaViewJobs();

    jobs.value = response?.data || [];
    preparaDados(jobs.value);

    alteraDisable(false);

};

/*==================================================================*/
/*================== FILTROS DE JOB E CLIENTE ======================*/
/*==================================================================*/

const dadoCodigoFiltro = ref('');
const dadoClienteFiltro = ref('');

const dadoCodigoFiltroValido = (codigo) => {

    if(dadoCodigoFiltro.value === ''){
        return true;
    }

    if(codigo != dadoCodigoFiltro.value){
        return false;
    }

    return true;

};

const dadoClienteFiltroValido = (clienteNomeFantasia) => {

    if(dadoClienteFiltro.value === ''){
        return true;
    }

    if(clienteNomeFantasia != dadoClienteFiltro.value){
        return false;
    }

    return true;

};

/*==================================================================*/
/*================== DADOS FILTRADOS ===============================*/
/*==================================================================*/

const dadosFiltrados = computed(() => {

    return dados?.value?.reduce((acc, dado) => {

        if(!dadoCodigoFiltroValido(dado?.codigo)){
            return acc;
        }

        if(!dadoClienteFiltroValido(dado?.cliente?.nomeFantasia)){
            return acc;
        }

        acc.push(dado);

        return acc;

    }, []);

});

/*==================================================================*/
/*================== ALTERA FILTROS ================================*/
/*==================================================================*/

const alteraDadoCodigoFiltro = (dado) => {

    dadoCodigoFiltro.value = dado?.codigo || '';

};

const alteraDadoClienteFiltro = (dado) => {

    dadoClienteFiltro.value = dado?.cliente?.nomeFantasia || '';

};

/*==================================================================*/
/*================== FILTROS CONFIGURADOS ==========================*/
/*==================================================================*/

const filtros = computed(() => [

    { label: 'N° do Job', items: dados.value, itemText: 'codigo',                onChange: alteraDadoCodigoFiltro },
    { label: 'Cliente',   items: dados.value, itemText: 'cliente.nomeFantasia',  onChange: alteraDadoClienteFiltro },

]);

/*==================================================================*/
/*================== HEADERS DA TABELA =============================*/
/*==================================================================*/

const dadosHeaders = [
    { text: 'N° do Job',      value: 'codigo',                        type: 'string' },
    { text: 'Status',         value: 'status',                        type: 'string' },
    { text: 'Descrição',      value: 'descricao',                     type: 'string' },
    { text: 'Cliente',        value: 'cliente.nomeFantasia',          type: 'string' },
    { text: 'Data Prevista',  value: 'dataFinalContratual',           type: 'date' },
    { text: 'Data Efetiva',   value: 'dataFinalEfetiva',              type: 'date' },
];

/*==================================================================*/
/*================== VALIDAÇÃO DO BOTÃO FINALIZAR ==================*/
/*==================================================================*/

const disabledBotaoFinalizarJob = computed(() => {

    if(disable.value){
        return true;
    }

    if(!selecionado.value?.id){
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== VALIDAÇÃO DE NEGÓCIO DO JOB ===================*/
/*==================================================================*/

const validacaoDoJob = ref(null);

const validaJob = (dadosBrutos = {}) => {

    const parcelas = dadosBrutos?.parcelas || [];
    const pedidosDeCompra = dadosBrutos?.pedidosDeCompra || [];
    const ordensDePagamento = dadosBrutos?.ordensDePagamento || [];

    const falhas = [];

    //==================================================================//
    //========== VALIDA SE POSSUI PARCELA, PEDIDO OU ORDEM =============//
    //==================================================================//

    if(UtilArray.areEmpty(parcelas, pedidosDeCompra, ordensDePagamento)){
        falhas.push('Job não possui nenhuma Parcela, Pedido de Compra ou Ordem de Pagamento');
    }

    //==================================================================//
    //================ VALIDA PARCELAS DO JOB ==========================//
    //==================================================================//

    parcelas.forEach((parcela) => {

        if((parcela?.valorParaReceber ?? 0) !== 0){
            falhas.push(`Parcela "${parcela?.codigo}" ainda não foi paga`);
        }

    });

    //==================================================================//
    //============ VALIDA PEDIDOS DE COMPRA DO JOB =====================//
    //==================================================================//

    pedidosDeCompra.forEach((pedidoDeCompra) => {

        if(pedidoDeCompra?.status !== "Finalizado"){
            falhas.push(`Pedido de Compra "${pedidoDeCompra?.codigo}" ainda não foi finalizado`);
        }

    });

    //==================================================================//
    //=========== VALIDA ORDENS DE PAGAMENTO DO JOB ====================//
    //==================================================================//

    ordensDePagamento.forEach((ordemDePagamento) => {

        if(ordemDePagamento?.situacao !== "Pago"){
            falhas.push(`Ordem de Pagamento "${ordemDePagamento?.codigo}" ainda não foi paga`);
        }

    });

    //==================================================================//
    //====================== MONTA O RESULTADO =========================//
    //==================================================================//

    return {
        valido: UtilArray.isEmpty(falhas),
        falhas,
    };

};

const validaJobSelecionado = async () => {

    const jobID = selecionado.value?.id;

    const response = await JobFinalizarService.pegaViewValidacaoDoJob(jobID);

    validacaoDoJob.value = validaJob(response?.data || {});

    return validacaoDoJob.value;

};

/*==================================================================*/
/*================== AO FINALIZAR JOB ==============================*/
/*==================================================================*/

const aoFinalizarJob = async () => {

    if(disabledBotaoFinalizarJob.value){
        return;
    }

    alteraDisable(true);

    const validacao = await validaJobSelecionado();

    alteraDisable(false);

    if(!validacao?.valido){
        await modalErroValidacaoJobRef.value?.abrirModal();
        return;
    }

    await modalConfirmacaoFinalizarJobRef.value?.abrirModal();

};

/*==================================================================*/
/*================== CONFIRMA FINALIZAÇÃO DO JOB ===================*/
/*==================================================================*/

const confirmaFinalizacaoDoJob = async () => {

    alteraDisable(true);

    const jobID = selecionado.value?.id;

    await JobFinalizarService.finalizaJob(jobID);

    selecionado.value = {};
    validacaoDoJob.value = null;

    await loadJobs();

    alteraDisable(false);

    await modalConfirmacaoFinalizarJobRef.value?.fecharModal();

};

/*==================================================================*/
/*================== ON MOUNTED ====================================*/
/*==================================================================*/

onMounted(async () => {

    await loadJobs();

});

</script>

<template>

    <TelaTabela
        :disabled="disable"
        :filtros="filtros"
        :headers="dadosHeaders"
        :dados="dadosFiltrados"
        selecao="unico"
        :selecionado="selecionado"
        paginacao="paginada"
        @update:selecionado="alteraSelecionado"
    >

        <template #tela-titulo>

            Job Finalizar

        </template>

        <template #toolbar-baixo-direita>

            <BotaoTextoBase
                class="c-botao-finalizar-job"
                :disabled="disabledBotaoFinalizarJob"
                :click="aoFinalizarJob"
            >
                Finalizar Job
            </BotaoTextoBase>

        </template>

        <template #modais>

            <ModalConfirmacaoFinalizarJob
                ref="modalConfirmacaoFinalizarJobRef"
                :disabled="disable"
                :job="selecionado"
                :confirmar="confirmaFinalizacaoDoJob"
            />

            <ModalErroValidacaoJob
                ref="modalErroValidacaoJobRef"
                :disabled="disable"
                :job="selecionado"
                :falhas="validacaoDoJob?.falhas"
            />

        </template>

    </TelaTabela>

</template>

<style scoped>

.c-botao-finalizar-job{
    width: 100%;
}

</style>
